import React, { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../LanguageContext';

function Quote({ onToggle }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [showPanel, setShowPanel] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    product: '',
    brand: '',
    quantity: '',
    details: '',
    description: '',
    location: '',
    website: '',
    _formStartedAt: Date.now(),
  });

  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const panelRef = useRef(null);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setShowPanel(false);
      }
    };

    if (showPanel) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPanel]);

  const handleAuthClick = () => {
    if (!showPanel) {
      setFormData((current) => ({ ...current, _formStartedAt: Date.now() }));
    }
    setShowPanel(!showPanel);
    setErrorMessage('');
    setSuccessMessage('');
    if (onToggle) onToggle(false); // Close login panel if open
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitQuote = async () => {
    setErrorMessage('');
    setSuccessMessage('');
    setIsSubmitting(true);

    if (!formData.email || !formData.name || !formData.product || !formData.quantity || !formData.location) {
      setErrorMessage(t('Please fill in all required fields (*)', 'Complete todos los campos obligatorios (*)'));
      setIsSubmitting(false);
      return;
    }

    // Sanitize
    const sanitizedData = Object.keys(formData).reduce((acc, key) => {
      acc[key] = DOMPurify.sanitize(formData[key]);
      return acc;
    }, {});

    try {
      const res = await fetch('https://valleyapi-dr9y.onrender.com/api/quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sanitizedData),
      });

      const data = await res.json();

      if (res.ok) {
        setSuccessMessage(t('Quote request sent successfully! We will contact you soon.', '¡La solicitud de cotización se envió correctamente! Nos comunicaremos pronto.'));
        // Reset form
        setFormData({
          email: '', name: '', product: '', brand: '', quantity: '',
          details: '', description: '', location: '', website: '', _formStartedAt: Date.now()
        });
        setShowPanel(false);
        navigate('/thank-you?type=quote');
      } else {
        setErrorMessage(data.error || t('Failed to send quote. Please try again.', 'No se pudo enviar la cotización. Inténtelo de nuevo.'));
      }
    } catch (err) {
      console.error('❌ Quote submission error:', err);
      setErrorMessage(t('Server connection error. Please try again later.', 'Error de conexión con el servidor. Inténtelo más tarde.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={quoteContainerStyles}>
      <span onClick={handleAuthClick} style={authTextStyles}>
        {t('QUOTE', 'COTIZAR')}
      </span>

      {showPanel && (
        <div ref={panelRef} style={panelStyles}>
          <div style={panelHeaderStyles}>
            <h3 style={{ margin: '0', color: '#e67e22' }}>{t('Request a Quote', 'Solicitar una cotización')}</h3>
            <button onClick={() => setShowPanel(false)} style={closeButtonStyles}>
              ✕
            </button>
          </div>

          <div style={formContainerStyles}>
            <div style={honeypotStyles} aria-hidden="true">
              <label htmlFor="quote-website">{t('Leave this field empty', 'Deje este campo vacío')}</label>
              <input id="quote-website" type="text" name="website" value={formData.website} onChange={handleInputChange} tabIndex={-1} autoComplete="off" />
            </div>
            <input type="email" name="email" placeholder="Email *" value={formData.email} onChange={handleInputChange} style={inputStyles} maxLength="254" required />
            <input type="text" name="name" placeholder={t('Full Name *', 'Nombre completo *')} value={formData.name} onChange={handleInputChange} style={inputStyles} maxLength="100" required />
            <input type="text" name="product" placeholder={t('Product *', 'Producto *')} value={formData.product} onChange={handleInputChange} style={inputStyles} maxLength="200" required />
            <input type="text" name="brand" placeholder={t('Brand', 'Marca')} value={formData.brand} onChange={handleInputChange} style={inputStyles} maxLength="120" />
            <input type="number" name="quantity" placeholder={t('Quantity *', 'Cantidad *')} value={formData.quantity} onChange={handleInputChange} style={inputStyles} min="1" max="1000000" required />
            <input type="text" name="details" placeholder={t('Details (size, model, etc.)', 'Detalles (tamaño, modelo, etc.)')} value={formData.details} onChange={handleInputChange} style={inputStyles} maxLength="500" />
            <textarea name="description" placeholder={t('Description / Additional Information', 'Descripción / Información adicional')} value={formData.description} onChange={handleInputChange} style={{ ...inputStyles, height: '80px', resize: 'vertical' }} maxLength="4000" />
            <input type="text" name="location" placeholder={t('Location (City, Country) *', 'Ubicación (Ciudad, País) *')} value={formData.location} onChange={handleInputChange} style={inputStyles} maxLength="150" required />

            {errorMessage && <p style={errorStyles}>{errorMessage}</p>}
            {successMessage && <p style={successStyles}>{successMessage}</p>}

            <button
              onClick={handleSubmitQuote}
              disabled={isSubmitting}
              style={{
                ...quoteButtonStyles,
                opacity: isSubmitting ? 0.7 : 1,
                cursor: isSubmitting ? 'not-allowed' : 'pointer'
              }}
            >
              {isSubmitting ? t('Sending...', 'Enviando...') : t('Send Quote', 'Enviar cotización')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Styles
const quoteContainerStyles = {
  position: 'relative',
  display: 'inline-block',
  padding: '5px 0px',
};

const authTextStyles = {
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 'bold',
  color: '#fff',
  backgroundColor: '#e67e22',
  padding: '5px 10px',
  borderRadius: '5px',
};

const panelStyles = {
  position: 'absolute',
  top: '100%',
  right: 0,
  backgroundColor: '#fff',
  border: '1px solid #ccc',
  borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  zIndex: 100,
  width: '340px',
  maxHeight: '85vh',
  overflowY: 'auto',
};

const panelHeaderStyles = {
  padding: '15px 20px',
  borderBottom: '1px solid #eee',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: '#f8f9fa',
  borderTopLeftRadius: '8px',
  borderTopRightRadius: '8px',
};

const closeButtonStyles = {
  background: 'none',
  border: 'none',
  fontSize: '18px',
  cursor: 'pointer',
  color: '#666',
  padding: '0 5px',
};

const formContainerStyles = {
  padding: '20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

const honeypotStyles = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
};

const inputStyles = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: '4px',
  border: '1px solid #ccc',
  boxSizing: 'border-box',
  fontSize: '14px',
};

const quoteButtonStyles = {
  backgroundColor: '#e67e22',
  color: 'white',
  padding: '12px 15px',
  border: 'none',
  borderRadius: '4px',
  fontSize: '16px',
  fontWeight: 'bold',
  width: '100%',
  marginTop: '10px',
  cursor: 'pointer',
};

const errorStyles = {
  color: 'red',
  fontSize: '13px',
  margin: '5px 0',
};

const successStyles = {
  color: 'green',
  fontSize: '13px',
  margin: '5px 0',
};

export default Quote;
